import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Modal, Space, Form, Input, Select, InputNumber,
    Tag, Typography,
} from 'antd';
import {
    FolderAddOutlined, CheckCircleOutlined,
    ApartmentOutlined, DatabaseOutlined, ApiOutlined,
} from '@ant-design/icons';
import { useKeycloak } from '@react-keycloak/web';
import {
    getEmbeddingModels,
} from '../../services/api';
import { useUserSession } from '../../contexts/UserSessionContext';
import SegmentedToggle from '../SegmentedToggle';
import EmbeddingModelField from '../models/EmbeddingModelField';
import LlmModelPickerSection from '../models/LlmModelPickerSection';
import ChatRetrievalStrategySection, { DEFAULT_CLASSIC_MAX_PRIOR_USER_LINES } from './ChatRetrievalStrategySection';
import RagClassicPriorLinesField from './RagClassicPriorLinesField';
import RagQueryRewriteAdvancedOptions from './RagQueryRewriteAdvancedOptions';
import useCloudProviders from '../../hooks/useCloudProviders';
import useLlmModelPicker from '../../hooks/useLlmModelPicker';

const { Text, Paragraph } = Typography;
const { Option } = Select;

const IndexingMethodCards = ({ value, onChange }) => (
    <div className="create-collection-choice-grid create-collection-indexing-cards">
        <button
            type="button"
            className={`create-collection-choice-card ${value === 'vector' ? 'is-selected' : ''}`}
            onClick={() => onChange?.('vector')}
            aria-pressed={value === 'vector'}
        >
            <div className="create-collection-choice-card-icon create-collection-choice-card-icon-vector">
                <DatabaseOutlined aria-hidden />
            </div>
            <div className="create-collection-choice-card-body">
                <span className="create-collection-choice-card-title">Semantic search</span>
                <span className="create-collection-choice-card-desc">Turns documents into embeddings for fast similarity search.</span>
                <span className="create-collection-choice-card-hint">Best for most knowledge bases and Q&amp;A.</span>
            </div>
            {value === 'vector' && <CheckCircleOutlined className="create-collection-choice-card-check" aria-hidden />}
        </button>
        <button
            type="button"
            className={`create-collection-choice-card ${value === 'pageindex' ? 'is-selected' : ''}`}
            onClick={() => onChange?.('pageindex')}
            aria-pressed={value === 'pageindex'}
        >
            <div className="create-collection-choice-card-icon create-collection-choice-card-icon-pageindex">
                <ApartmentOutlined aria-hidden />
            </div>
            <div className="create-collection-choice-card-body">
                <span className="create-collection-choice-card-title">PageIndex</span>
                <span className="create-collection-choice-card-desc">Structured index with model-guided section lookup. No chunking step.</span>
                <span className="create-collection-choice-card-hint">Best for structured documents (manuals, specs, policies).</span>
            </div>
            {value === 'pageindex' && <CheckCircleOutlined className="create-collection-choice-card-check" aria-hidden />}
        </button>
        <button
            type="button"
            className={`create-collection-choice-card ${value === 'hybrid' ? 'is-selected' : ''}`}
            onClick={() => onChange?.('hybrid')}
            aria-pressed={value === 'hybrid'}
        >
            <div className="create-collection-choice-card-icon create-collection-choice-card-icon-hybrid">
                <ApiOutlined aria-hidden />
            </div>
            <div className="create-collection-choice-card-body">
                <span className="create-collection-choice-card-title">Hybrid</span>
                <span className="create-collection-choice-card-desc">Finds answers by meaning and by exact words: names, codes, IDs, and specific phrases.</span>
                <span className="create-collection-choice-card-hint">Combines semantic (vector) search with full-text keyword search; ranked results are merged.</span>
            </div>
            {value === 'hybrid' && <CheckCircleOutlined className="create-collection-choice-card-check" aria-hidden />}
        </button>
    </div>
);

const ChunkMetricInput = ({ value, onChange, ...rest }) => (
    <div className="chunk-metric-input">
        <InputNumber
            className="chunk-metric-input-number"
            value={value}
            onChange={onChange}
            {...rest}
        />
        <span className="chunk-metric-suffix">characters</span>
    </div>
);

const ChunkingMethodCards = ({ value, onChange, methods }) => (
    <div className="create-collection-choice-grid create-collection-chunking-grid">
        {methods.map((m) => {
            const isRec = m.id === 'recursive';
            const selected = value != null && String(value) === String(m.id);
            return (
                <button
                    key={m.id}
                    type="button"
                    className={`create-collection-choice-card create-collection-chunking-card ${selected ? 'is-selected' : ''}`}
                    onClick={() => onChange?.(m.id)}
                    aria-pressed={selected}
                >
                    <div className="create-collection-choice-card-body">
                        <span className="create-collection-choice-card-title">
                            {m.name.replace(/\s*\(Recommended\)\s*/i, '').trim()}
                            {isRec && <Tag color="blue" className="create-collection-recommended-tag">Recommended</Tag>}
                        </span>
                        {m.description && (
                            <span className="create-collection-chunking-desc">{m.description}</span>
                        )}
                    </div>
                    {selected && <CheckCircleOutlined className="create-collection-choice-card-check" aria-hidden />}
                </button>
            );
        })}
    </div>
);

const CreateCollectionModal = ({ open, onCancel, onCreate }) => {
    const navigate = useNavigate();
    const { keycloak } = useKeycloak();
    const { groupsMap } = useUserSession();
    const [form] = Form.useForm();
    const [creating, setCreating] = useState(false);

    const [embeddingModels, setEmbeddingModels] = useState([]);
    const [chunkingMethods, setChunkingMethods] = useState([]);
    const [retrievalStrategy, setRetrievalStrategy] = useState('simple');
    const [classicMaxPriorUserLines, setClassicMaxPriorUserLines] = useState(DEFAULT_CLASSIC_MAX_PRIOR_USER_LINES);
    const [ragRewriteMax, setRagRewriteMax] = useState(10);

    const groupsList = useMemo(
        () => Object.entries(groupsMap).map(([id, name]) => ({ id: Number(id), name })),
        [groupsMap]
    );

    const indexingMethod = Form.useWatch('indexing_method', form) ?? 'vector';
    const visibility = Form.useWatch('visibility', form) ?? 'private';

    const { cloudProviders } = useCloudProviders(keycloak, { enabled: open });

    const pageindexPicker = useLlmModelPicker({
        keycloak,
        cloudProviders,
        enabled: open && indexingMethod === 'pageindex',
        initial: { is_local: false },
    });

    const rewritePicker = useLlmModelPicker({
        keycloak,
        cloudProviders,
        enabled: open && (indexingMethod === 'vector' || indexingMethod === 'hybrid') && retrievalStrategy === 'query_rewrite',
        initial: { is_local: false },
    });

    useEffect(() => {
        if (indexingMethod === 'pageindex') {
            pageindexPicker.setModel('');
        }
    }, [indexingMethod]);

    useEffect(() => {
        if (!open || !keycloak?.authenticated) return;
        getEmbeddingModels(keycloak).then(m => setEmbeddingModels(m)).catch(() => {});
        const apiBase = process.env.REACT_APP_ENV === 'PRODUCTION' ? '/api' : 'http://localhost:8000';
        fetch(`${apiBase}/data-collections/chunking-methods/`, { headers: { 'Authorization': `Bearer ${keycloak.token}` } })
            .then(r => r.ok ? r.json() : []).then(setChunkingMethods).catch(() => {});
    }, [open, keycloak]);

    useEffect(() => {
        if (!open) {
            pageindexPicker.reset();
            rewritePicker.reset();
            setRetrievalStrategy('simple');
            setClassicMaxPriorUserLines(DEFAULT_CLASSIC_MAX_PRIOR_USER_LINES);
            setRagRewriteMax(10);
            form.resetFields();
        }
    }, [open, form]);

    const chunkingMethodsList = chunkingMethods.length > 0 ? chunkingMethods : [{ id: 'recursive', name: 'Recursive (Recommended)', description: 'Splits text using multiple separators. Best for general use.' }];

    const handleFinish = async (values) => {
        if (values.indexing_method === 'pageindex' && !(pageindexPicker.model || '').trim()) {
            form.setFields([{ name: 'indexing_method', errors: ['Enter a model name (local) or model id (cloud).'] }]);
            return;
        }
        setCreating(true);
        try {
            const formData = new FormData();
            formData.append('name', values.name.trim());
            if (values.description?.trim()) formData.append('description', values.description.trim());
            formData.append('indexing_method', values.indexing_method);

            if (values.indexing_method === 'vector' || values.indexing_method === 'hybrid') {
                formData.append('embedding_model_id', values.embedding_model_id.toString());
                formData.append('chunking_method', values.chunking_method || 'recursive');
                formData.append('chunk_size', (values.chunk_size || 512).toString());
                formData.append('chunk_overlap', (values.chunk_overlap || 50).toString());
                formData.append(
                    'rag_semantic_dual_query_mode',
                    retrievalStrategy === 'dual_semantic' ? 'true' : 'false',
                );
                formData.append(
                    'rag_query_rewrite_mode',
                    retrievalStrategy === 'query_rewrite' ? 'true' : 'false',
                );
                if (retrievalStrategy === 'query_rewrite') {
                    formData.append('rag_query_rewrite_max_messages', String(ragRewriteMax));
                    if (rewritePicker.source === 'cloud' && rewritePicker.providerId && (rewritePicker.model || '').trim()) {
                        formData.append('rag_query_rewrite_provider_id', String(rewritePicker.providerId));
                        formData.append('rag_query_rewrite_model', rewritePicker.model.trim());
                    } else if (rewritePicker.source === 'local' && (rewritePicker.model || '').trim()) {
                        formData.append('rag_query_rewrite_model', rewritePicker.model.trim());
                    }
                }
                if (retrievalStrategy === 'simple') {
                    formData.append('rag_classic_max_prior_user_lines', String(classicMaxPriorUserLines));
                }
            } else {
                formData.append('pageindex_model', pageindexPicker.model.trim());
                if (pageindexPicker.source === 'cloud' && pageindexPicker.providerId) {
                    formData.append('pageindex_provider_id', pageindexPicker.providerId.toString());
                }
            }
            if (values.visibility === 'team' && values.group_id) {
                formData.append('group_id', values.group_id.toString());
            }
            await onCreate(formData, values.name.trim());
        } finally {
            setCreating(false);
        }
    };

    const visibilityOptions = [
        { value: 'private', text: 'Only me' },
        { value: 'team', text: 'Team' },
    ];

    const modalWidth = 720;

    return (
        <Modal
            title={<Space><FolderAddOutlined style={{ color: 'var(--color-warning)' }} /><span>Create New Collection</span></Space>}
            open={open}
            onCancel={onCancel}
            onOk={() => form.submit()}
            confirmLoading={creating}
            okText="Create Collection"
            width={modalWidth}
            className="create-collection-modal"
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                initialValues={{
                    chunking_method: 'recursive',
                    chunk_size: 512,
                    chunk_overlap: 50,
                    visibility: 'private',
                    indexing_method: 'vector',
                }}
            >
                <Form.Item name="name" label="Collection Name" rules={[{ required: true, message: 'Please enter a collection name' }]}>
                    <Input placeholder="e.g., Product Documentation, Q3 Reports..." size="large" />
                </Form.Item>
                <Form.Item name="description" label="Description">
                    <Input.TextArea placeholder="Optional description for this collection..." rows={2} />
                </Form.Item>

                <Form.Item
                    name="indexing_method"
                    label="Indexing method"
                    rules={[{ required: true, message: 'Choose how documents are indexed' }]}
                >
                    <IndexingMethodCards />
                </Form.Item>

                {(indexingMethod === 'vector' || indexingMethod === 'hybrid') && (
                    <>
                        <Form.Item
                            name="embedding_model_id"
                            label="Embedding model"
                            rules={[{ required: true, message: 'Please select an embedding model' }]}
                        >
                            <EmbeddingModelField
                                models={embeddingModels}
                                onModelsUpdated={setEmbeddingModels}
                            />
                        </Form.Item>

                        <Form.Item
                            name="chunking_method"
                            label="How to split documents"
                            rules={[{ required: true, message: 'Choose a chunking method' }]}
                        >
                            <ChunkingMethodCards methods={chunkingMethodsList} />
                        </Form.Item>

                        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.chunking_method !== cur.chunking_method}>
                            {({ getFieldValue }) => {
                                const method = getFieldValue('chunking_method');
                                if (method !== 'fixed_size' && method !== 'recursive') return null;
                                return (
                                    <div className="form-item-grid create-collection-chunk-metrics-grid">
                                        <Form.Item name="chunk_size" label="Chunk size">
                                            <ChunkMetricInput min={100} max={4000} />
                                        </Form.Item>
                                        <Form.Item name="chunk_overlap" label="Overlap">
                                            <ChunkMetricInput min={0} max={500} />
                                        </Form.Item>
                                    </div>
                                );
                            }}
                        </Form.Item>

                        <Form.Item label="Chat search">
                            <ChatRetrievalStrategySection
                                compact
                                value={retrievalStrategy}
                                onChange={setRetrievalStrategy}
                                disabled={creating}
                            >
                                {retrievalStrategy === 'simple' && (
                                    <RagClassicPriorLinesField
                                        value={classicMaxPriorUserLines}
                                        onChange={setClassicMaxPriorUserLines}
                                        disabled={creating}
                                    />
                                )}
                                {retrievalStrategy === 'query_rewrite' && (
                                    <div className="create-collection-rag-rewrite-advanced">
                                        <RagQueryRewriteAdvancedOptions
                                            compact
                                            maxMessages={ragRewriteMax}
                                            onMaxMessagesChange={setRagRewriteMax}
                                            picker={rewritePicker}
                                            cloudProviders={cloudProviders}
                                            navigate={navigate}
                                            disabled={creating}
                                        />
                                    </div>
                                )}
                            </ChatRetrievalStrategySection>
                        </Form.Item>
                    </>
                )}

                {indexingMethod === 'pageindex' && (
                    <Form.Item label="Model source">
                        <LlmModelPickerSection
                            picker={pageindexPicker}
                            cloudProviders={cloudProviders}
                            navigate={navigate}
                            disabled={creating}
                            modelStepLabel="LLM model for PageIndex"
                            cloudEmptyDescription="No chat models for this provider. Try catalog sync or another provider."
                        />
                    </Form.Item>
                )}

                <Form.Item label="Visibility">
                    <div className="create-collection-visibility-block">
                        <Form.Item name="visibility" noStyle>
                            <SegmentedToggle
                                className="create-collection-visibility-toggle"
                                options={visibilityOptions}
                            />
                        </Form.Item>
                        <Text type="secondary" className="create-collection-visibility-hint">
                            {visibility === 'team'
                                ? 'Choose a group below. Members can use this collection according to their access.'
                                : 'Only you can see and manage this collection.'}
                        </Text>
                    </div>
                </Form.Item>

                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.visibility !== cur.visibility}>
                    {({ getFieldValue }) => getFieldValue('visibility') === 'team' ? (
                        <Form.Item name="group_id" label="Group">
                            <Select placeholder="Select a group" showSearch optionFilterProp="children" size="large">
                                {groupsList.map(g => <Option key={g.id} value={g.id}>{g.name}</Option>)}
                            </Select>
                        </Form.Item>
                    ) : null}
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default CreateCollectionModal;
